import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '~/modules/elements/akui/button';
import { Menu } from '~/modules/elements/akui/menu';
import { captureAvatar } from '~/modules/user-profiles/avatar-capture';

interface Props {
  onCapture: (dataUrl: string) => void;
  onCancel: () => void;
}

export default function WebcamCapture({ onCapture, onCancel }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    // `navigator` directly rather than `userMediaService`: that service tracks *microphone* access
    // status, and a camera prompt answered here must not be reported as the mic being granted or
    // refused.
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } } })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
          setReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not access the camera.');
      });

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Mirrored to match the preview, which is `scale-x-[-1]`.
    const dataUrl = captureAvatar(video, { mirrored: true });
    if (!dataUrl) return;

    streamRef.current?.getTracks().forEach((track) => track.stop());
    onCapture(dataUrl);
  }, [onCapture]);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative size-48 overflow-hidden rounded-full bg-black">
        <video
          ref={videoRef}
          className="h-full w-full scale-x-[-1] object-cover"
          autoPlay
          playsInline
          muted
          data-test="avatar-preview"
        />
        {!ready && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/50">
            Starting camera…
          </div>
        )}
      </div>
      {error && <Menu.HelpText className="text-center">{error}</Menu.HelpText>}
      <Menu.ButtonGroup className="gap-2">
        <Button size="small" onClick={capture} disabled={!ready} data-test="take-photo">
          Take photo
        </Button>
        <Button size="small" onClick={onCancel} data-test="cancel-photo">
          Cancel
        </Button>
      </Menu.ButtonGroup>
    </div>
  );
}
